
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO, startOfMonth } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMeHistory } from "@/hooks/use-me-queries";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { StatusLegend, statusColorClass } from "@/components/attendance/attendance-cell";
import type { CellStatus } from "@/lib/types";
import { formatDateDisplay, todayIST } from "@/lib/timezone";
import { RAISE_CUTOFF_MESSAGE, canRaiseForDate } from "@/lib/raise-window";

function monthKey(d: Date): string {
  return format(d, "yyyy-MM");
}

function cellLabel(status: CellStatus, lateMinutes?: number | null): string {
  if (status === "L" && lateMinutes != null) return `L${lateMinutes}`;
  if (status === "pending") return "—";
  return status;
}

export default function MeHistoryPage() {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(() =>
    startOfMonth(parseISO(todayIST()))
  );
  const [feedback, setFeedback] = useState<string | null>(null);
  const month = monthKey(cursor);
  const { data, isLoading, error } = useMeHistory(month);

  const daysByDate = useMemo(() => {
    const map = new Map<string, { status: CellStatus; late_minutes?: number | null }>();
    data?.days.forEach((d) => map.set(d.date, d));
    return map;
  }, [data]);

  const year = cursor.getFullYear();
  const monthIndex = cursor.getMonth();
  const firstDay = new Date(year, monthIndex, 1);
  const startPad = firstDay.getDay();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

  const cells: (number | null)[] = [
    ...Array(startPad).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  function handleDayClick(dateStr: string) {
    if (canRaiseForDate(dateStr)) {
      setFeedback(null);
      navigate(`/me/notice?dates=${dateStr}`, {
        state: { dates: [dateStr] },
      });
      return;
    }
    setFeedback(
      `Too late to submit a notice for ${formatDateDisplay(dateStr)}. ${RAISE_CUTOFF_MESSAGE}`
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">History</h2>
        <p className="text-sm text-muted-foreground">
          Tap today to request sick, official leave, or permission. Older dates
          are closed after 4:30am the next morning.
        </p>
      </div>

      <StatusLegend compact />

      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          size="icon"
          onClick={() => {
            setFeedback(null);
            setCursor(new Date(year, monthIndex - 1, 1));
          }}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <p className="font-medium">{format(cursor, "MMMM yyyy")}</p>
        <Button
          variant="outline"
          size="icon"
          onClick={() => {
            setFeedback(null);
            setCursor(new Date(year, monthIndex + 1, 1));
          }}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {isLoading && (
        <p className="text-center text-muted-foreground">Loading…</p>
      )}
      {error && (
        <p className="text-center text-red-600">Could not load history.</p>
      )}

      {feedback && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {feedback}
        </p>
      )}

      <div className="rounded-xl border bg-white p-3">
        <div className="mb-2 grid grid-cols-7 gap-1 text-center text-xs text-muted-foreground">
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
            <div key={d}>{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((day, idx) => {
            if (day === null) {
              return <div key={`pad-${idx}`} />;
            }
            const dateStr = `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
            const record = daysByDate.get(dateStr);
            const status = record?.status ?? "pending";
            const raisable = canRaiseForDate(dateStr);
            const isToday = dateStr === todayIST();

            return (
              <button
                key={dateStr}
                type="button"
                onClick={() => handleDayClick(dateStr)}
                className={cn(
                  "relative z-10 flex w-full cursor-pointer flex-col items-center gap-0.5 rounded-md p-1",
                  "hover:bg-saffron-50 active:bg-saffron-100",
                  !raisable && "opacity-80",
                  isToday && "ring-2 ring-saffron-500 bg-saffron-50"
                )}
              >
                <span
                  className={cn(
                    "text-xs text-muted-foreground",
                    isToday && "font-semibold text-saffron-800"
                  )}
                >
                  {day}
                </span>
                <span
                  className={cn(
                    "pointer-events-none flex h-8 w-8 items-center justify-center rounded border text-xs font-bold",
                    statusColorClass(status)
                  )}
                >
                  {cellLabel(status, record?.late_minutes)}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap justify-center gap-3 text-xs">
        <Legend label="Present" className="bg-emerald-100 text-emerald-800 border-emerald-200" />
        <Legend label="Late" className="bg-amber-100 text-amber-900 border-amber-200" />
        <Legend label="Absent" className="bg-red-100 text-red-800 border-red-200" />
        <Legend label="Official leave (O)" className="bg-sky-100 text-sky-800 border-sky-200" />
        <Legend label="Sick (S)" className="bg-rose-100 text-rose-800 border-rose-200" />
        <Legend label="Permission (TP)" className="bg-teal-100 text-teal-800 border-teal-200" />
        <Legend label="Mantras (MS)" className="bg-indigo-100 text-indigo-800 border-indigo-200" />
        <Legend label="Fine (FS)" className="bg-orange-100 text-orange-900 border-orange-200" />
        <Legend label="Holiday (H)" className="bg-violet-100 text-violet-800 border-violet-200" />
        <Legend label="Pending" className="bg-slate-100 text-slate-500 border-slate-200" />
      </div>
    </div>
  );
}

function Legend({ label, className }: { label: string; className: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={cn("inline-block h-4 w-4 rounded border", className)} />
      {label}
    </span>
  );
}
